import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { getMeOptions } from '#modules/user/model/me';
import {
  getNativeAuthUserId,
  nativeSessionMark,
  onNativeAuthReady,
} from '#features/api/utils/nativeAuth';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { useNotificationsStore } from '../model/notificationsStore';
import { getPushDeviceSignal } from './deviceSettings';
import { getPushPlatform } from './nativePlatform';
import {
  prepareNativePushRegistration,
  refreshNativePushRegistration,
  registerNativePushToken,
} from './nativePushRegistration';

/**
 * Keeps the backend's FCM token current. FCM can rotate a device's registration token at any
 * time (reinstall, data cleared, restore to a new device, server-side refresh); the old token
 * then goes dead. We PUT the token once at enable time, so a rotation while the app is open
 * would leave the backend pushing to a dead token → missed notifications until the next
 * relaunch happens to re-register.
 *
 * This mounts a PERSISTENT `registration` listener that re-PUTs on any token change, and — on
 * startup for an already-enabled user — re-registers so a rotation that happened while the app
 * was closed is reconciled too (permission is already granted, so no dialog). No-op on web or
 * when notifications aren't enabled. Mount once, high in the tree.
 */
export const useNativePushTokenSync = () => {
  const status = useNotificationsStore((s) => s.status);
  const isAuth = useIsAuth();
  // Whose device this currently is. A phone outlives an account: sign out, hand it to someone
  // else, sign back in — the FCM token is identical every time, and the backend stores exactly
  // one owner per token. Without the id in the signature below, a device that changed hands
  // keeps its registration pointed at the previous account and simply stops ringing.
  const { data: userId } = useQuery({
    ...getMeOptions(),
    enabled: isAuth,
    select: (data) => (data?.__typename === 'CurrentUser' ? data.id : null),
  });

  useEffect(() => {
    if (!envConfig.IS_NATIVE || status !== 'ENABLED' || !userId) {
      return;
    }

    let remove: (() => void) | undefined;
    let removeAuthReady: (() => void) | undefined;
    let active = true;

    void (async () => {
      const credential = await nativeSessionMark();
      const currentOwner = await getNativeAuthUserId();
      if (!active || currentOwner !== userId) return;
      const context = prepareNativePushRegistration(userId, credential);
      if (!context) return;
      const { PushNotifications } = await import('@capacitor/push-notifications');
      if (!active) return;

      const handle = await PushNotifications.addListener('registration', (token) => {
        const value = token.value;
        if (!active || !value) {
          return;
        }
        void (async () => {
          const device = await getPushDeviceSignal();
          const currentCredential = await nativeSessionMark();
          const currentOwner = await getNativeAuthUserId();
          if (!active || currentOwner !== userId) return;
          const currentContext = refreshNativePushRegistration(context, currentCredential);
          if (!currentContext) return;
          // What we last told the backend about this device, and on whose behalf. Re-sending
          // only on a changed token would freeze these facts forever: the token rarely rotates,
          // while whether a call can cover the lock screen changes the moment the user grants
          // that permission — or an OS update takes it away again — and the owner changes on
          // every account switch.
          const signature = `${userId}|${value}|${device.deviceOem ?? ''}|${
            device.appVersion ?? ''
          }|${device.callScreenReady ?? ''}`;
          if (signature === localStorage.getItem('fcm_token_signature')) {
            return;
          }
          try {
            const platform = await getPushPlatform();
            if (!active) return;
            await registerNativePushToken(currentContext, {
              input: { token: value, platform, ...device },
              isActive: () => active,
              onRegistered: () => {
                localStorage.setItem('fcm_token', value);
                localStorage.setItem('fcm_token_signature', signature);
              },
            });
          } catch {
            // transient — the next registration event / relaunch retries
          }
        })();
      });

      if (!active) {
        void handle.remove();
        return;
      }
      remove = () => void handle.remove();
      removeAuthReady = onNativeAuthReady(() => {
        if (active) void PushNotifications.register().catch(() => undefined);
      });

      // Reconcile a token that rotated while the app was closed: re-register re-emits the
      // current token through the listener above (no dialog — permission already granted).
      try {
        await PushNotifications.register();
      } catch {
        // ignore — already registered / transient
      }
    })();

    return () => {
      active = false;
      remove?.();
      removeAuthReady?.();
    };
    // userId is a dependency, not a detail: re-running on an account switch is what re-registers
    // this device for its new owner.
  }, [status, userId]);
};
